import { useFeatureFlags } from '@datacamp/exercise-feature-flags';
import { isError } from '@datacamp/exercise-library-utils';
import { isAiFeatureEnabled } from '@datacamp/le-shared-components';
import { useEffect, useState } from 'react';
import { groupHubApiClient } from '../api/groupHubApiClient';
export const ERROR_EXPLANATION_FLAG = 'exp_wl_explain_error_button';
export const INCORRECT_SUBMISSION_FLAG = 'cp--incorrect-submission-ai-hints';
export const ERROR_EXPLANATION_EXPERIMENT_FLAG = 'cp--error-explanation-ab-test';
export const SOLUTION_EXPLANATION_FLAG = 'cp--explain-solution';
const fallbackUserGroupSettings = {
  aiCoursesExercisesEnabled: false,
  aiErrorExplanationEnabled: false,
  aiSolutionExplanationEnabled: false,
  isUserB2B: false,
  isUserFromDatacamp: false
};
const getUserGroupSettings = async _ref => {
  let {
    environment,
    sentry,
    userId
  } = _ref;
  try {
    const getGroupsResult = await groupHubApiClient(environment).getUserGroupSettings({
      userId
    });
    if (isError(getGroupsResult)) {
      sentry.captureError(getGroupsResult.error, {
        message: 'Error while fetching B2B AI flags'
      });
      return fallbackUserGroupSettings;
    }
    const validGroups = getGroupsResult.value.filter(_ref2 => {
      let {
        status
      } = _ref2;
      return status !== 'cancelled';
    });
    return {
      aiCoursesExercisesEnabled: validGroups.every(_ref3 => {
        let {
          aiCoursesExercisesEnabled
        } = _ref3;
        return aiCoursesExercisesEnabled;
      }),
      aiErrorExplanationEnabled: validGroups.every(_ref4 => {
        let {
          aiErrorExplanationEnabled
        } = _ref4;
        return aiErrorExplanationEnabled;
      }),
      aiSolutionExplanationEnabled: validGroups.every(_ref5 => {
        let {
          aiSolutionExplanationEnabled
        } = _ref5;
        return aiSolutionExplanationEnabled;
      }),
      isUserB2B: getGroupsResult.value.some(_ref6 => {
        let {
          groupType
        } = _ref6;
        return groupType === 'enterprise';
      }),
      isUserFromDatacamp: getGroupsResult.value.some(_ref7 => {
        let {
          name
        } = _ref7;
        return name.includes('DataCamp');
      })
    };
  } catch (error) {
    sentry.captureError(error, {
      message: 'Error while fetching B2B AI flags'
    });
    return fallbackUserGroupSettings;
  }
};
export const initialUserAiFeaturesEnablement = {
  aiExercises: false,
  errorExplanation: false,
  incorrectSubmission: false,
  isErrorExplanationExperiment: false,
  loading: true,
  solutionExplanation: false
};
export const useUserAiFeaturesEnablement = _ref8 => {
  let {
    courseId,
    environment,
    exerciseType,
    sentry,
    userId
  } = _ref8;
  const [userAiFeaturesEnablement, setUserAiFeaturesEnablement] = useState(initialUserAiFeaturesEnablement);
  const [userGroupSettings, setUserGroupSettings] = useState(null);
  const {
    flags,
    loading
  } = useFeatureFlags([ERROR_EXPLANATION_FLAG, INCORRECT_SUBMISSION_FLAG, ERROR_EXPLANATION_EXPERIMENT_FLAG, SOLUTION_EXPLANATION_FLAG]);
  useEffect(() => {
    if (userGroupSettings === null) {
      const setAiFlagsHelper = async () => {
        setUserGroupSettings(await getUserGroupSettings({
          environment,
          sentry,
          userId
        }));
      };
      setAiFlagsHelper();
    }
  }, [userGroupSettings, environment, userId, sentry]);
  useEffect(() => {
    if (!loading && userGroupSettings !== null) {
      const isErrorExplanationExperiment = flags[ERROR_EXPLANATION_EXPERIMENT_FLAG] === 'on';
      const userEnabledForErrorExplanation = userGroupSettings.aiErrorExplanationEnabled && flags[ERROR_EXPLANATION_FLAG] === 'on';
      const userEnabledForSolutionExplanation = userGroupSettings.aiSolutionExplanationEnabled && flags[SOLUTION_EXPLANATION_FLAG] === 'on';
      const userEnabledForIncorrectSubmissions = isAiFeatureEnabled({
        courseId,
        exerciseType,
        feature: 'aiHints',
        isB2BFlagEnabled: userGroupSettings.aiErrorExplanationEnabled,
        isSplitFlagEnabled: flags[INCORRECT_SUBMISSION_FLAG] === 'on',
        isUserB2B: userGroupSettings.isUserB2B,
        isUserFromDatacamp: userGroupSettings.isUserFromDatacamp
      });
      setUserAiFeaturesEnablement({
        aiExercises: userGroupSettings.aiCoursesExercisesEnabled,
        errorExplanation: userEnabledForErrorExplanation,
        incorrectSubmission: userEnabledForIncorrectSubmissions,
        isErrorExplanationExperiment,
        loading,
        solutionExplanation: userEnabledForSolutionExplanation
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGroupSettings, loading]);
  return userAiFeaturesEnablement;
};